/**
 * TPG Player — Media Asset model (Sprint 8 Phase B1).
 *
 * The player never assumes "a video." Every playback request is a Media Asset.
 * Presentation strategy is derived from asset metadata; the platform decides
 * entitlement / what may play; TPG Player decides how it is presented.
 */

/** First-class presentation kinds (extensible). */
export var MediaPresentationKind = Object.freeze({
  VIDEO: "video",
  LIVE: "live",
  SCHEDULED_LIVE: "scheduled_live",
  CHANNEL: "channel",
  AUDIO: "audio",
  IMAGE_AUDIO: "image_audio",
  PLAYLIST: "playlist",
  PODCAST: "podcast",
  AUDIOBOOK: "audiobook",
  ALBUM: "album",
  EMBED: "embed",
  EXTERNAL: "external",
  INTERACTIVE: "interactive",
  SPATIAL: "spatial",
  UNKNOWN: "unknown",
});

/** Origin / delivery source classification (orthogonal to presentation kind). */
export var MediaSourceType = Object.freeze({
  HOSTED: "hosted",
  LIVE_INGEST: "live_ingest",
  YOUTUBE: "youtube",
  VIMEO: "vimeo",
  EXTERNAL_URL: "external_url",
  EXTERNAL_EMBED: "external_embed",
  COMPANION: "companion",
  UNKNOWN: "unknown",
});

export var MediaLoopMode = Object.freeze({
  NONE: "none",
  AUDIO_ONLY: "audio_only",
  PRESENTATION: "presentation",
  ITEM: "item",
  COLLECTION: "collection",
});

export var MediaSurfaceKind = Object.freeze({
  VIDEO: "video",
  EMBED: "embed",
  AUDIO: "audio",
  IMAGE: "image",
  COMPOSITE: "composite",
  CHANNEL: "channel",
  PLAYLIST: "playlist",
});

/**
 * @typedef {object} MediaResource
 * @property {string} [url]
 * @property {string} [mimeType]
 * @property {string} [role]
 * @property {number} [width]
 * @property {number} [height]
 * @property {number} [durationSeconds]
 * @property {object} [headers]
 */

/**
 * @typedef {object} CompanionImageAudioConfig
 * @property {MediaResource|null} [image]
 * @property {MediaResource|null} [audio]
 * @property {string} [loopMode]
 * @property {boolean} [audioOptional]
 * @property {string} [fit]
 * @property {string} [background]
 * @property {boolean} [animated]
 * @property {MediaResource|null} [transcript]  future lyrics / transcript
 */

/**
 * @typedef {object} MediaCollectionRef
 * @property {string} [id]
 * @property {string} [kind]  playlist | album | conference | course | channel
 * @property {string[]} [itemIds]
 * @property {number} [startIndex]
 */

/**
 * @typedef {object} MediaScheduleRef
 * @property {string} [eventStartAt]
 * @property {string} [eventEndAt]
 * @property {string} [channelId]
 * @property {boolean} [countdown]
 * @property {number} [dvrWindowSeconds]
 */

/**
 * @typedef {object} MediaAsset
 * @property {string|null} [id]
 * @property {string} presentationKind
 * @property {string} [sourceType]
 * @property {string} [title]
 * @property {string} [description]
 * @property {MediaResource|null} [primary]
 * @property {CompanionImageAudioConfig|null} [companion]
 * @property {MediaCollectionRef|null} [collection]
 * @property {MediaScheduleRef|null} [schedule]
 * @property {object} [metadata]
 * @property {string[]} [tags]
 * @property {string[]} [capabilities]  requested capability module ids
 */

/**
 * @param {Partial<MediaAsset>} input
 * @returns {MediaAsset}
 */
export function createMediaAsset(input) {
  input = input || {};
  return {
    id: input.id != null ? input.id : null,
    presentationKind: input.presentationKind || MediaPresentationKind.UNKNOWN,
    sourceType: input.sourceType || MediaSourceType.UNKNOWN,
    title: input.title || "",
    description: input.description || "",
    primary: input.primary || null,
    companion: input.companion || null,
    collection: input.collection || null,
    schedule: input.schedule || null,
    metadata: input.metadata || {},
    tags: Array.isArray(input.tags) ? input.tags.slice() : [],
    capabilities: Array.isArray(input.capabilities)
      ? input.capabilities.slice()
      : [],
  };
}

/**
 * @param {Partial<CompanionImageAudioConfig>} input
 * @returns {CompanionImageAudioConfig}
 */
export function createCompanionImageAudio(input) {
  input = input || {};
  return {
    image: input.image || null,
    audio: input.audio || null,
    loopMode: input.loopMode || MediaLoopMode.NONE,
    audioOptional: input.audioOptional !== false,
    fit: input.fit || "contain",
    background: input.background || "transparent",
    animated: !!input.animated,
    transcript: input.transcript || null,
  };
}

/**
 * Infer presentation kind from Media Asset or legacy video_details.
 * @param {object} details
 * @returns {string}
 */
export function inferPresentationKind(details) {
  details = details || {};
  if (details.presentationKind) return details.presentationKind;
  var metaKind =
    (details.metadata && details.metadata.presentation_kind) ||
    details.presentation_kind ||
    "";
  if (metaKind === "live") return MediaPresentationKind.LIVE;
  if (metaKind === "image" || metaKind === "image_audio") {
    return MediaPresentationKind.IMAGE_AUDIO;
  }
  if (metaKind === "video") return MediaPresentationKind.VIDEO;

  if (details.collection && details.collection.kind === "channel") {
    return MediaPresentationKind.CHANNEL;
  }
  if (details.collection && details.collection.itemIds) {
    var ck = details.collection.kind;
    if (ck === "album") return MediaPresentationKind.ALBUM;
    if (ck === "podcast") return MediaPresentationKind.PODCAST;
    if (ck === "audiobook") return MediaPresentationKind.AUDIOBOOK;
    return MediaPresentationKind.PLAYLIST;
  }

  if (details.schedule && (details.schedule.channelId || details.is_live)) {
    if (details.schedule.eventStartAt && details.schedule.countdown) {
      return MediaPresentationKind.SCHEDULED_LIVE;
    }
    if (details.is_live || details.schedule.channelId) {
      return MediaPresentationKind.LIVE;
    }
  }
  if (details.is_live) return MediaPresentationKind.LIVE;

  if (details.companion && (details.companion.image || details.companion.audio)) {
    return MediaPresentationKind.IMAGE_AUDIO;
  }
  if (details.audio_url || details.audioUrl) {
    if (details.companion_image || details.companionImage || details.image_url) {
      return MediaPresentationKind.IMAGE_AUDIO;
    }
    return MediaPresentationKind.AUDIO;
  }

  var url =
    (details.primary && details.primary.url) ||
    details.video_url ||
    details.videoUrl ||
    details.input_url ||
    details.inputUrl ||
    details.hls_url ||
    details.hlsUrl ||
    "";
  var lower = String(url).toLowerCase();
  if (
    lower.indexOf("youtube.com") !== -1 ||
    lower.indexOf("youtu.be") !== -1 ||
    details.video_type == 2
  ) {
    return MediaPresentationKind.EMBED;
  }
  if (lower.indexOf("vimeo.com") !== -1 || details.video_type == 4) {
    return MediaPresentationKind.EMBED;
  }

  if (url) return MediaPresentationKind.VIDEO;
  return MediaPresentationKind.UNKNOWN;
}

/**
 * Infer source type from asset / legacy details.
 * @param {object} details
 * @returns {string}
 */
export function inferSourceType(details) {
  details = details || {};
  if (details.sourceType) return details.sourceType;
  var url =
    (details.primary && details.primary.url) ||
    details.video_url ||
    details.videoUrl ||
    details.input_url ||
    details.inputUrl ||
    "";
  var lower = String(url).toLowerCase();
  if (lower.indexOf("youtube.com") !== -1 || lower.indexOf("youtu.be") !== -1) {
    return MediaSourceType.YOUTUBE;
  }
  if (lower.indexOf("vimeo.com") !== -1) return MediaSourceType.VIMEO;
  if (details.companion) return MediaSourceType.COMPANION;
  if (details.is_live) return MediaSourceType.LIVE_INGEST;
  if (url) return MediaSourceType.HOSTED;
  return MediaSourceType.UNKNOWN;
}

/**
 * Normalize legacy video_details into a Media Asset (non-destructive).
 * @param {object} details
 * @param {object} [mediaMeta]  from resolveMediaFromVideoDetails
 * @returns {MediaAsset}
 */
export function mediaAssetFromVideoDetails(details, mediaMeta) {
  details = details || {};
  mediaMeta = mediaMeta || {};
  var kind = inferPresentationKind(details);
  var companion =
    details.companion ||
    (details.companion_image_url || details.companion_audio_url
      ? createCompanionImageAudio({
          image: details.companion_image_url
            ? { url: details.companion_image_url, role: "image" }
            : details.default_image
              ? { url: details.default_image, role: "image" }
              : null,
          audio: details.companion_audio_url
            ? { url: details.companion_audio_url, role: "audio" }
            : null,
          loopMode: details.loop_mode || "none",
        })
      : null);
  if (companion && (kind === "video" || kind === "unknown")) {
    kind = MediaPresentationKind.IMAGE_AUDIO;
  }
  var isCompanion =
    kind === MediaPresentationKind.IMAGE_AUDIO ||
    kind === MediaPresentationKind.AUDIO;
  var sourceType = isCompanion
    ? MediaSourceType.COMPANION
    : inferSourceType(
        Object.assign({}, details, {
          primary: mediaMeta.src ? { url: mediaMeta.src } : details.primary,
        })
      );
  return createMediaAsset({
    id: details.admin_video_id || details.id || details.unique_id || null,
    presentationKind: kind,
    sourceType: sourceType,
    title: details.title || details.name || "",
    description: details.description || "",
    primary:
      isCompanion
        ? companion && companion.image
          ? { url: companion.image.url, role: "primary" }
          : null
        : mediaMeta.src
          ? {
              url: mediaMeta.src,
              role: "primary",
              mimeType: mediaMeta.type || null,
            }
          : details.primary || null,
    companion: companion,
    collection: details.collection || null,
    schedule: details.schedule || null,
    metadata: {
      video_type: details.video_type,
      presentation_kind: kind,
      legacy: true,
    },
    tags: [],
  });
}

/**
 * @param {MediaAsset|object} asset
 * @returns {boolean}
 */
export function isNonVideoPresentation(asset) {
  var kind =
    (asset && asset.presentationKind) || inferPresentationKind(asset || {});
  return (
    kind === MediaPresentationKind.AUDIO ||
    kind === MediaPresentationKind.IMAGE_AUDIO ||
    kind === MediaPresentationKind.PODCAST ||
    kind === MediaPresentationKind.AUDIOBOOK ||
    kind === MediaPresentationKind.ALBUM
  );
}

/**
 * Whether native (non-embed) chrome should be suppressed for ToS.
 * @param {MediaAsset|object} asset
 * @returns {boolean}
 */
export function requiresNativeChrome(asset) {
  var st = (asset && asset.sourceType) || inferSourceType(asset || {});
  return st === MediaSourceType.YOUTUBE || st === MediaSourceType.VIMEO;
}

export default {
  MediaPresentationKind,
  MediaSourceType,
  MediaLoopMode,
  MediaSurfaceKind,
  createMediaAsset,
  createCompanionImageAudio,
  inferPresentationKind,
  inferSourceType,
  mediaAssetFromVideoDetails,
  isNonVideoPresentation,
  requiresNativeChrome,
};
